import { RefObject, useEffect } from 'react';
import { useTheme } from 'styled-components';

/**
 * Mirrors StormUI's default `theme.inlinePortalZIndexIncrement`. `inlinePortalZIndexIncrement`
 * is an optional escape hatch that is not part of StormUI's exported `Theme` type and is
 * undefined in the stock theme, so it is read through a narrow optional type with this default.
 */
const DEFAULT_INLINE_PORTAL_Z_INDEX_INCREMENT = 10000;

/**
 * Escalates a portal host element's z-index above its nearest ancestor stacking context,
 * a local reimplementation of StormUI's `InlinePortalProvider`/`adoptAncestorZIndex` (neither
 * is publicly exported from `@amzn/storm-ui`; only `PortalContextProvider` is).
 *
 * The host element MUST be `position: relative` (or a flex item) for the assigned z-index to
 * form a stacking context — `adoptAncestorZIndex` sets only `z-index`, and a bare z-index on a
 * static, non-flex element is ignored.
 *
 * Used for the global chrome (header, sidenav) which is elevated above a StormUI `FocusOverlay`:
 * popovers portalled into an escalated host adopt `nearest-ancestor + increment`, so they paint
 * above the chrome, while nested popovers escalate again relative to the host.
 */
export const useEscalatedPortalZIndex = (ref: RefObject<HTMLElement>): void => {
  const theme = useTheme();

  useEffect(() => {
    const node = ref.current;
    if (typeof window === 'undefined' || !node) {
      return;
    }
    const increment =
      (theme as { inlinePortalZIndexIncrement?: number } | undefined)
        ?.inlinePortalZIndexIncrement ??
      DEFAULT_INLINE_PORTAL_Z_INDEX_INCREMENT;
    let ancestorZIndex = 0;
    let parent = node.parentElement;
    while (parent) {
      const zIndexString = window
        .getComputedStyle(parent)
        .getPropertyValue('z-index');
      const zIndexNumber = parseInt(zIndexString, 10);
      // A purely numeric (non-`auto`) computed z-index marks the nearest stacking context.
      if (zIndexString === zIndexNumber.toString()) {
        ancestorZIndex = zIndexNumber;
        break;
      }
      parent = parent.parentElement;
    }
    node.style.setProperty('z-index', `${ancestorZIndex + increment}`);
  }, [ref, theme]);
};

export default useEscalatedPortalZIndex;
